import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as memorizationApi from '@/features/memorization/api';
import type {
  ActivityView,
  CoverageView,
  CreateCorrectionInput,
  CreatePersonalTargetInput,
  CreateSubmissionInput,
  CreateTargetInput,
  EvaluateSubmissionInput,
  EvaluationResult,
  ProgressSummaryView,
  SegmentView,
  StudentProgressView,
  SubmissionView,
  TargetView,
} from '@/features/memorization/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import type { PagedData } from '@/lib/api/types';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/**
 * Tenant-scoped query keys embed the Active Organization id AND the
 * relevant student/target id (ADR-026 Addendum, AO-9) - Org A's cached
 * targets/progress cannot structurally satisfy an Org B query, and
 * switching organizations can never leave a stale student's data visible.
 */
const targetsListKey = (
  organizationId: string | null,
  studentId: string,
  page: number,
  limit: number,
) => [TENANT_QUERY_PREFIX, organizationId, 'targets', 'list', studentId, page, limit] as const;
const targetDetailKey = (organizationId: string | null, targetId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'targets', 'detail', targetId] as const;
const targetCoverageKey = (organizationId: string | null, targetId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'targets', 'coverage', targetId] as const;
const studentProgressKey = (organizationId: string | null, studentId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'progress', studentId] as const;
const classProgressKey = (organizationId: string | null, classId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'progress', 'classes', classId] as const;
const learningSpaceProgressKey = (organizationId: string | null, learningSpaceId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'progress', 'learning-spaces', learningSpaceId] as const;
const organizationProgressKey = (organizationId: string | null) =>
  [TENANT_QUERY_PREFIX, organizationId, 'progress', 'organization'] as const;
const submissionsListKey = (
  organizationId: string | null,
  studentId: string,
  page: number,
  limit: number,
) => [TENANT_QUERY_PREFIX, organizationId, 'submissions', 'list', studentId, page, limit] as const;
const submissionDetailKey = (organizationId: string | null, submissionId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'submissions', 'detail', submissionId] as const;
/** Distinct from `submissionsListKey` (per-student) - this is the org-wide, unscoped list (Phase G-3). */
const pendingEvaluationsKey = (organizationId: string | null, page: number, limit: number) =>
  [TENANT_QUERY_PREFIX, organizationId, 'submissions', 'pending-evaluation', page, limit] as const;
const evaluationsListKey = (
  organizationId: string | null,
  targetId: string,
  page: number,
  limit: number,
  submissionId: string | undefined,
) =>
  [
    TENANT_QUERY_PREFIX,
    organizationId,
    'evaluations',
    'list',
    targetId,
    page,
    limit,
    submissionId ?? null,
  ] as const;

export function useTargets(
  studentId: string,
  page = 1,
  limit = 20,
): UseQueryResult<PagedData<TargetView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: targetsListKey(activeOrganizationId, studentId, page, limit),
    queryFn: () => memorizationApi.listTargets(studentId, page, limit),
    enabled: activeOrganizationId !== null && studentId.length > 0,
  });
}

export function useTarget(targetId: string): UseQueryResult<TargetView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: targetDetailKey(activeOrganizationId, targetId),
    queryFn: () => memorizationApi.getTarget(targetId),
    enabled: activeOrganizationId !== null,
  });
}

export function useTargetCoverage(targetId: string): UseQueryResult<CoverageView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: targetCoverageKey(activeOrganizationId, targetId),
    queryFn: () => memorizationApi.getTargetCoverage(targetId),
    enabled: activeOrganizationId !== null,
  });
}

export function useCreateTarget(
  studentId: string,
): UseMutationResult<TargetView, ApiError, CreateTargetInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CreateTargetInput) => memorizationApi.createTarget(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'targets', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: studentProgressKey(activeOrganizationId, studentId),
      });
    },
  });
}

/**
 * `POST /memorization/personal-targets` (ADR-031, Decision 6). Invalidates
 * exactly what `useCreateTarget` does - the same target list and the same
 * student progress summary - since it writes the same row through the
 * same service; only the authorization precondition differs.
 */
export function useCreatePersonalTarget(
  studentId: string,
): UseMutationResult<TargetView, ApiError, CreatePersonalTargetInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CreatePersonalTargetInput) => memorizationApi.createPersonalTarget(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'targets', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: studentProgressKey(activeOrganizationId, studentId),
      });
    },
  });
}

export function useCancelTarget(studentId: string): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (targetId: string) => memorizationApi.cancelTarget(targetId),
    onSuccess: (_data, targetId) => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'targets', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: targetDetailKey(activeOrganizationId, targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: targetCoverageKey(activeOrganizationId, targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: studentProgressKey(activeOrganizationId, studentId),
      });
    },
  });
}

/**
 * The daily activity trend (ADR-034). Bounded window, computed live
 * server-side; the browser's timezone offset travels with the request so
 * the buckets are the days the reader actually experienced.
 */
export function useStudentActivity(
  studentId: string,
  days = 30,
): UseQueryResult<ActivityView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: [...studentProgressKey(activeOrganizationId, studentId), 'activity', days] as const,
    queryFn: () => memorizationApi.getStudentActivity(studentId, days),
    enabled: activeOrganizationId !== null && studentId.length > 0,
  });
}

export function useStudentProgress(
  studentId: string,
): UseQueryResult<StudentProgressView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: studentProgressKey(activeOrganizationId, studentId),
    queryFn: () => memorizationApi.getStudentProgress(studentId),
    enabled: activeOrganizationId !== null && studentId.length > 0,
  });
}

export function useClassProgress(classId: string): UseQueryResult<ProgressSummaryView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: classProgressKey(activeOrganizationId, classId),
    queryFn: () => memorizationApi.getClassProgress(classId),
    enabled: activeOrganizationId !== null,
  });
}

export function useLearningSpaceProgress(
  learningSpaceId: string,
): UseQueryResult<ProgressSummaryView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: learningSpaceProgressKey(activeOrganizationId, learningSpaceId),
    queryFn: () => memorizationApi.getLearningSpaceProgress(learningSpaceId),
    enabled: activeOrganizationId !== null,
  });
}

/**
 * `enabled` lets a caller that already knows the role cannot read this
 * aggregate skip the request entirely. Rules of hooks force the hook to be
 * called before any early return in the component, so without this the
 * query fires and is refused with a 403 even when nothing is rendered
 * (`organization-progress-card.tsx`).
 */
export function useOrganizationProgress(
  enabled = true,
): UseQueryResult<ProgressSummaryView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: organizationProgressKey(activeOrganizationId),
    queryFn: () => memorizationApi.getOrganizationProgress(activeOrganizationId as string),
    enabled: activeOrganizationId !== null && enabled,
  });
}

/**
 * On-demand reconciliation (ADR-024 Decision 5). Invalidates all three
 * aggregate levels - a single organization-wide rebuild can change every
 * class/learning-space summary that rolls up into it, not just the
 * organization row itself.
 */
export function useReconcileOrganizationProgress(): UseMutationResult<void, ApiError, void> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: () => memorizationApi.reconcileOrganizationProgress(activeOrganizationId as string),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'progress'],
      });
    },
  });
}

export function useSubmissions(
  studentId: string,
  page = 1,
  limit = 20,
): UseQueryResult<PagedData<SubmissionView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: submissionsListKey(activeOrganizationId, studentId, page, limit),
    queryFn: () => memorizationApi.listSubmissions(studentId, page, limit),
    enabled: activeOrganizationId !== null && studentId.length > 0,
  });
}

/**
 * Org-wide submissions awaiting evaluation (Phase G-3, Teacher Workspace).
 * Gated by `enabled` - the caller must pass its own
 * `memorization_targets:manage` capability check, matching the same
 * enabled-gate pattern `useMurajaahQueue` already established, so a
 * non-broad caller (Student/Parent) triggers zero requests rather than
 * relying on the backend's Self/Child narrowing to quietly return a
 * differently-scoped result under the same UI framing.
 */
export function usePendingEvaluations(
  page = 1,
  limit = 20,
  enabled = true,
): UseQueryResult<PagedData<SubmissionView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: pendingEvaluationsKey(activeOrganizationId, page, limit),
    queryFn: () =>
      memorizationApi.listSubmissions(undefined, page, limit, { status: 'AWAITING_EVALUATION' }),
    enabled: activeOrganizationId !== null && enabled,
  });
}

export function useSubmission(submissionId: string): UseQueryResult<SubmissionView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: submissionDetailKey(activeOrganizationId, submissionId),
    queryFn: () => memorizationApi.getSubmission(submissionId),
    enabled: activeOrganizationId !== null && submissionId.length > 0,
  });
}

export function useCreateSubmission(
  studentId: string,
): UseMutationResult<SubmissionView, ApiError, CreateSubmissionInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CreateSubmissionInput) => memorizationApi.createSubmission(input),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'submissions', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: targetDetailKey(activeOrganizationId, created.targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: targetCoverageKey(activeOrganizationId, created.targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: studentProgressKey(activeOrganizationId, studentId),
      });
    },
  });
}

export function useEvaluateSubmission(
  studentId: string,
  submissionId: string,
  targetId: string,
): UseMutationResult<EvaluationResult, ApiError, EvaluateSubmissionInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: EvaluateSubmissionInput) =>
      memorizationApi.evaluateSubmission(submissionId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: submissionDetailKey(activeOrganizationId, submissionId),
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'submissions', 'list', studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'submissions', 'pending-evaluation'],
      });
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'evaluations', 'list', targetId],
      });
      void queryClient.invalidateQueries({
        queryKey: targetDetailKey(activeOrganizationId, targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: targetCoverageKey(activeOrganizationId, targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: studentProgressKey(activeOrganizationId, studentId),
      });
    },
  });
}

export function useCreateCorrection(
  studentId: string,
  targetId: string,
): UseMutationResult<EvaluationResult, ApiError, CreateCorrectionInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: CreateCorrectionInput) => memorizationApi.createCorrection(targetId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TENANT_QUERY_PREFIX, activeOrganizationId, 'evaluations', 'list', targetId],
      });
      void queryClient.invalidateQueries({
        queryKey: targetDetailKey(activeOrganizationId, targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: targetCoverageKey(activeOrganizationId, targetId),
      });
      void queryClient.invalidateQueries({
        queryKey: studentProgressKey(activeOrganizationId, studentId),
      });
    },
  });
}

export function useEvaluations(
  targetId: string,
  page = 1,
  limit = 20,
  submissionId?: string,
): UseQueryResult<PagedData<SegmentView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: evaluationsListKey(activeOrganizationId, targetId, page, limit, submissionId),
    queryFn: () =>
      memorizationApi.listEvaluations(
        targetId,
        page,
        limit,
        submissionId !== undefined ? { submissionId } : {},
      ),
    enabled: activeOrganizationId !== null && targetId.length > 0,
  });
}
